/**
 * Teams that are not one of today's 30: a franchise under a former name
 * (the Seattle SuperSonics, the Charlotte Bobcats), an international club in
 * a preseason game (Hapoel Jerusalem, Real Madrid), and the All-Star squads
 * (Team LeBron, World). They reach the app through a player's career table,
 * game log and box scores, and through old draft classes.
 *
 * Each is shown with its own crest and code. A former name links through to
 * the club that carries the franchise today (DSPRT-149: the Sonics open the
 * Thunder, the Philadelphia Warriors open Golden State) — see `franchise`;
 * a club from outside the league, or one that folded, stays plain text.
 *
 * Crests are served from /crests. ESPN hosts the international clubs it covers
 * and the All-Star squads, but for former franchise names it attaches today's
 * logo (its 2008 Seattle record carries the Thunder's), so those come from
 * Wikipedia or SportsLogos.net, one per era where a team changed its logo;
 * the clubs ESPN has no logo for come from Wikipedia or TheSportsDB. Where no
 * source has a logo (only the Indianapolis Jets, today), `logo` is null and
 * the code shows on a tile.
 */
import { GL_MODE, isGlClub } from "./league";

export interface OtherTeam {
  /** The code shown beside the crest: the team's own, not today's club's. */
  code: string;
  name: string;
  /** Under /crests, or null when there is no logo to show. */
  logo: string | null;
  /** A separate version for dark grounds, where the logo has one. */
  darkLogo?: string;
  /** A version for the light site alone, where the logo's pale ink fades into the page (DSPRT-367). */
  lightLogo?: string;
  /** A black logo with no dark version: inverted on dark grounds. */
  invertOnDark?: boolean;
  /** A squarer mark for a row's sizes, where the logo is a wide wordmark that can't be read there (DSPRT-360). */
  compact?: string;
  /** The compact mark's own version for dark grounds; without one it follows invertOnDark. */
  compactDark?: string;
  /** Today's club for a former name ("SEA" → "OKC"); absent for a club that isn't one. */
  franchise?: string;
}

const t = (code: string, name: string, logo: string | null, extra: Partial<OtherTeam> = {}): OtherTeam => ({
  code, name, logo: logo && `/crests/${logo}.png`, ...extra,
  ...(extra.darkLogo ? { darkLogo: `/crests/${extra.darkLogo}.png` } : {}),
  ...(extra.lightLogo ? { lightLogo: `/crests/${extra.lightLogo}.png` } : {}),
  ...(extra.compact ? { compact: `/crests/${extra.compact}.png` } : {}),
  ...(extra.compactDark ? { compactDark: `/crests/${extra.compactDark}.png` } : {}),
});

/*
 * Former names, by the club that carries the franchise today and the last
 * season (by its closing year) each name, or each logo under that name, was
 * used. ESPN sends most of these under today's code (a 2006 Hornets season
 * comes as NOP, a 1995 Bullets one as WAS), so the season is what tells them
 * apart. `inv` marks a navy or black logo that is inverted on dark grounds.
 *
 * Sources. en.wikipedia (the file named):
 *   sea     File:Seattle SuperSonics logo.svg
 *   van     File:Vancouver Grizzlies logo.svg
 *   nok     File:NOKC Hornets logo.gif
 *   roc     File:Rochester Royals logo.svg
 *   cin     File:Cincinnati Royals logo.svg
 *   buf     File:Buffalo Braves Logo, 1972–1978.png
 *   sdc79   File:Los Angeles Clippers Logo, 1979-1982.png
 *   sdc     File:Los Angeles Clippers Logo, 1983-1984.png
 *   noj     File:New Orleans Jazz Logo, 1975-1979.png
 * sportslogos.net (the team's primary logo from the year in the file name,
 * which is the closing year of the first season it was used; white matte
 * around the older ones made transparent):
 *   njn79 njn91 njn98 nyn noh03 noh09 chh bob05 bob08 bob13 wsb75 wsb88 cap
 *   bal64 bal69 bal70 bal72 chp chz kck kco sdr sfw63 sfw70 phw47 phw52
 *   syr47 syr50 mnl ftw stl56 stl58 mlh52 mlh55 tri
 *
 * The Bullets' logo from 1968-69 to 1996-97 is a wordmark, a smear at a row's
 * size, so each has a compact mark (`-c`): the logo's own two hands reaching
 * for the ball, cut from SportsLogos.net's full-size file (DSPRT-360).
 * Baltimore's exist only small there, so theirs are Washington's drawing of
 * the same hands in each Baltimore logo's colours; 1971-72's blue is lifted
 * on dark grounds, where it sank.
 *
 * Two more wordmarks have one cut from the logo itself (DSPRT-367): New
 * Orleans's J, the note with the ball for its head, and St. Louis's hawk and
 * ball of 1955-57 with the wingtips trimmed. Each has an edged version for
 * dark grounds, white for the Jazz and cream for the Hawks.
 *
 * Fort Wayne's logo and the Warriors' of 1946-51 are pale drawings that fade
 * into the light site, so there each wears an edge in its own colour
 * (`-light`): red for the Pistons, blue for the Warriors.
 */
const inv = { invertOnDark: true };
const FORMER: Record<string, Array<[lastSeason: number, team: OtherTeam]>> = {
  OKC: [[2008, t("SEA", "Seattle SuperSonics", "sea")]],
  MEM: [[2001, t("VAN", "Vancouver Grizzlies", "van")]],
  BKN: [
    [1977, t("NYN", "New York Nets", "nyn")],
    [1990, t("NJN", "New Jersey Nets", "njn79")],
    [1997, t("NJN", "New Jersey Nets", "njn91")],
    [2012, t("NJN", "New Jersey Nets", "njn98")],
  ],
  NOP: [
    [2002, t("CHH", "Charlotte Hornets", "chh")],
    [2005, t("NOH", "New Orleans Hornets", "noh03")],
    [2007, t("NOK", "New Orleans/Oklahoma City Hornets", "nok")],
    [2008, t("NOH", "New Orleans Hornets", "noh03")],
    [2013, t("NOH", "New Orleans Hornets", "noh09")],
  ],
  // 2003 and 2004 are absent: Charlotte had no team for those two seasons.
  CHA: [
    [2002, t("CHH", "Charlotte Hornets", "chh")],
    [2007, t("CHA", "Charlotte Bobcats", "bob05")],
    [2012, t("CHA", "Charlotte Bobcats", "bob08")],
    [2014, t("CHA", "Charlotte Bobcats", "bob13")],
  ],
  WAS: [
    [1962, t("CHP", "Chicago Packers", "chp")],
    [1963, t("CHZ", "Chicago Zephyrs", "chz")],
    [1968, t("BAL", "Baltimore Bullets", "bal64")],
    [1969, t("BAL", "Baltimore Bullets", "bal69", { compact: "bal69-c" })],
    [1971, t("BAL", "Baltimore Bullets", "bal70", { compact: "bal70-c" })],
    [1972, t("BAL", "Baltimore Bullets", "bal72", { compact: "bal72-c", compactDark: "bal72-c-dark" })],
    [1973, t("BAL", "Baltimore Bullets", "bal70", { compact: "bal70-c" })],
    [1974, t("CAP", "Capital Bullets", "cap", { ...inv, compact: "cap-c" })],
    [1987, t("WSB", "Washington Bullets", "wsb75", { ...inv, compact: "wsb75-c" })],
    [1997, t("WSB", "Washington Bullets", "wsb88", { ...inv, compact: "wsb88-c" })],
  ],
  SAC: [
    [1957, t("ROC", "Rochester Royals", "roc")],
    [1972, t("CIN", "Cincinnati Royals", "cin")],
    [1975, t("KCO", "Kansas City-Omaha Kings", "kco")],
    [1985, t("KCK", "Kansas City Kings", "kck")],
  ],
  LAC: [
    [1978, t("BUF", "Buffalo Braves", "buf")],
    [1982, t("SDC", "San Diego Clippers", "sdc79")],
    [1984, t("SDC", "San Diego Clippers", "sdc")],
  ],
  UTA: [[1979, t("NOJ", "New Orleans Jazz", "noj", { compact: "noj-c", compactDark: "noj-c-dark" })]],
  HOU: [[1971, t("SDR", "San Diego Rockets", "sdr")]],
  GSW: [
    [1951, t("PHW", "Philadelphia Warriors", "phw47", { lightLogo: "phw47-light" })],
    [1962, t("PHW", "Philadelphia Warriors", "phw52")],
    [1969, t("SFW", "San Francisco Warriors", "sfw63")],
    [1971, t("SFW", "San Francisco Warriors", "sfw70")],
  ],
  PHI: [
    [1949, t("SYR", "Syracuse Nationals", "syr47")],
    [1963, t("SYR", "Syracuse Nationals", "syr50")],
  ],
  LAL: [[1960, t("MNL", "Minneapolis Lakers", "mnl")]],
  DET: [[1957, t("FTW", "Fort Wayne Pistons", "ftw", { lightLogo: "ftw-light" })]],
  ATL: [
    [1951, t("TRI", "Tri-Cities Blackhawks", "tri")],
    [1954, t("MLH", "Milwaukee Hawks", "mlh52")],
    [1955, t("MLH", "Milwaukee Hawks", "mlh55", inv)],
    [1957, t("STL", "St. Louis Hawks", "stl56", { ...inv, compact: "stl56-c", compactDark: "stl56-c-dark" })],
    [1968, t("STL", "St. Louis Hawks", "stl58")],
  ],
};

for (const [owner, eras] of Object.entries(FORMER)) for (const [, team] of eras) team.franchise = owner;
// ESPN files the 1988-2002 Hornets under the Pelicans, but the NBA gave that
// history back to Charlotte in 2014, and that's the club they open.
for (const [, team] of FORMER.NOP) if (team.code === "CHH") team.franchise = "CHA";

/*
 * Codes ESPN sends that are never one of today's 30, whatever the season:
 * former-name codes it still uses as-is, the international clubs found in
 * every NBA preseason from 2018 to 2026, and the All-Star Game squads.
 * Sources:
 *   espn (a.espncdn.com, the team's own logo, dark version where ESPN has
 *     one): mel, nzl, adl, mrc, cns and every All-Star squad
 *   en.wikipedia (the club article's logo): hapoel, real, mch, flmg, fbc,
 *     san, beijing, sha, perth, syd, ulm, sem, lon, mta, efes, mega, fener
 *   thesportsdb.com (the club's badge, Chinese CBA): guangzhou, ningbo,
 *     zhejiang, qingdao; (Spanish Liga ACB): valencia
 */
const FIXED: Record<string, OtherTeam> = {
  SEA: t("SEA", "Seattle SuperSonics", "sea", { franchise: "OKC" }),
  VAN: t("VAN", "Vancouver Grizzlies", "van", { franchise: "MEM" }),
  NJ: t("NJN", "New Jersey Nets", "njn98", { franchise: "BKN" }),
  NJN: t("NJN", "New Jersey Nets", "njn98", { franchise: "BKN" }),
  NOH: t("NOH", "New Orleans Hornets", "noh09", { franchise: "NOP" }),
  NOK: t("NOK", "New Orleans/Oklahoma City Hornets", "nok", { franchise: "NOP" }),
  // The NBA files the 1988-2002 Hornets' history under today's Charlotte club.
  CHH: t("CHH", "Charlotte Hornets", "chh", { franchise: "CHA" }),
  WSB: t("WSB", "Washington Bullets", "wsb88", { ...inv, compact: "wsb88-c", franchise: "WAS" }),

  // Clubs of the BAA and early NBA that folded, as Basketball-Reference codes
  // them (DSPRT-139). Crests from Wikipedia/Wikimedia Commons, white matte
  // made transparent (DSPRT-155): Anderson_Packers_logo.PNG,
  // Cleveland_Rebels_logo.png, Chicago_Stags_logo.png,
  // Washington_Capitols_logo.svg, Indianapolis_Olympians_logo.svg (a
  // wordmark, their only logo), "The logo of the original Baltimore Bullets
  // from the postwar era.gif", and the St. Louis Bombers' wordmark cut down to
  // its ball and net. These Baltimore Bullets folded in 1954 — not today's
  // Wizards, whose Baltimore years began in 1963 — so they link nowhere.
  // Sheboygan's is the figure from the badge TheSportsDB has for the club, the
  // only logo on record for it (none on Wikipedia or SportsLogos.net, so not
  // confirmed as the club's own): a thin red line drawing, its lines thickened
  // to read at a row's size, and on dark grounds drawn as it is with its inside
  // filled white (DSPRT-354).
  // The Olympians' wordmark can't be read at a row's size and they have no
  // other logo, so there a plain O in their navy stands for it, drawn for the
  // site and not the club's own (DSPRT-360).
  // Cleveland's is a wordmark too: at a row's size the script R from it, on
  // the logo's navy inside a red and white ring (DSPRT-367).
  // The chief from Wikimedia Commons, cut out; on dark grounds a white sticker edge keeps the ink lines readable (DSPRT-161).
  AND: t("AND", "Anderson Packers", "and", { darkLogo: "and-dark" }),
  BLB: t("BLB", "Baltimore Bullets", "blb", inv),
  CHS: t("CHS", "Chicago Stags", "chs"),
  CLR: t("CLR", "Cleveland Rebels", "clr", { compact: "clr-c" }),
  INO: t("INO", "Indianapolis Olympians", "ino", { ...inv, compact: "ino-o", compactDark: "ino-o" }),
  SHE: t("SHE", "Sheboygan Red Skins", "she", { darkLogo: "she-dark" }),
  STB: t("STB", "St. Louis Bombers", "stb"),
  WSC: t("WSC", "Washington Capitols", "wsc"),
  // The rest of the folded clubs, which showed a bare code until DSPRT-354.
  // Wikipedia: Waterloo_hawks_logo.png, "The logo of the original Denver
  // Nuggets.gif" (navy line art, its white paper made transparent),
  // Providence_Steamrollers_logo.svg, Torontohuskies.png. These Nuggets
  // folded in 1950 and are named with their season, so a search by name never
  // mistakes them for today's club.
  //
  // Three have no logo of their own on record (none on Wikipedia,
  // Basketball-Reference or SportsLogos.net), and what they show is not
  // confirmed as the club's (DSPRT-360). TheSportsDB's badges are modern
  // re-creations: the Ironmen's P pouring a basketball, as it is; the Falcons'
  // bird, which is drawn playing hockey there, cut to its head. Its badge for
  // the Jets reads "IND K's", the Kautskys they had been, so theirs is a plain
  // J in their red and navy, drawn for the site.
  // Toronto's wordmark is white and pale blue: on the light site it wears a
  // navy edge (DSPRT-367).
  DNN: t("DNN", "Denver Nuggets (1949–50)", "dnn", inv),
  DTF: t("DTF", "Detroit Falcons", "dtf"),
  INJ: t("INJ", "Indianapolis Jets", "inj"),
  PIT: t("PIT", "Pittsburgh Ironmen", "pit"),
  PRO: t("PRO", "Providence Steamrollers", "pro"),
  TRH: t("TRH", "Toronto Huskies", "trh", { lightLogo: "trh-light" }),
  WAT: t("WAT", "Waterloo Hawks", "wat"),

  HAPOEL: t("HAP", "Hapoel Jerusalem", "hapoel"),
  REAL: t("RMA", "Real Madrid", "real"),
  MCH: t("MCH", "Maccabi Haifa", "mch"),
  MRC: t("MRC", "Maccabi Ra'anana", "mrc", { invertOnDark: true }),
  FLMG: t("FLA", "Flamengo", "flmg"),
  FBC: t("FRA", "Franca", "fbc"),
  SAN: t("SLA", "San Lorenzo", "san"),
  BEIJING: t("BEI", "Beijing Ducks", "beijing"),
  SHA: t("SHA", "Shanghai Sharks", "sha"),
  GUANGZHOU: t("GZL", "Guangzhou Loong-Lions", "guangzhou"),
  // ESPN's code for them since their 2025 preseason games.
  GUA: t("GZL", "Guangzhou Loong-Lions", "guangzhou"),
  MEL: t("MEL", "Melbourne United", "mel", { darkLogo: "mel-dark" }),
  PERTH: t("PER", "Perth Wildcats", "perth"),
  SYD: t("SYD", "Sydney Kings", "syd"),
  NZL: t("NZB", "New Zealand Breakers", "nzl", { darkLogo: "nzl-dark" }),
  ADL: t("ADL", "Adelaide 36ers", "adl"),
  CNS: t("CNS", "Cairns Taipans", "cns"),
  ULM: t("ULM", "Ratiopharm Ulm", "ulm"),
  SEM: t("SEM", "South East Melbourne Phoenix", "sem", { invertOnDark: true }),
  LON: t("LON", "London Lions", "lon", { invertOnDark: true }),

  // Clubs that have played no NBA team, so ESPN has no code for them: a player
  // came to the league from one, and the move bar in his last games names it
  // as Basketball-Reference does (DSPRT-269) — see clubAbroad.
  NINGBO: t("NBR", "Ningbo Rockets", "ningbo"),
  ZHEJIANG: t("ZJL", "Zhejiang Lions", "zhejiang"),
  QINGDAO: t("QDE", "Qingdao Eagles", "qingdao"),
  MTA: t("MTA", "Maccabi Tel Aviv", "mta"),
  EFES: t("EFS", "Anadolu Efes", "efes"),
  MEGA: t("MEG", "Mega Basket", "mega"),
  VALENCIA: t("VAL", "Valencia Basket", "valencia"),
  FENER: t("FEN", "Fenerbahçe", "fener"),

  USA: t("USA", "Team USA", "usa", { darkLogo: "usa-dark" }),
  // World's letters are navy and sank into a dark ground: there they are drawn light, the globe as it is (DSPRT-371).
  WORLD: t("WLD", "Team World", "world", { darkLogo: "world-dark", compact: "world-c", compactDark: "world-c" }),
  GIA: t("GIA", "Team Giannis", "gia", { darkLogo: "gia-dark" }),
  LEB: t("LEB", "Team LeBron", "leb", { darkLogo: "leb-dark" }),
  // 2018, the first draft-picked game: ESPN files the other side as STE (B-040).
  STE: t("STE", "Team Stephen", "ste", { darkLogo: "ste-dark" }),
  DUR: t("DUR", "Team Durant", "dur", { darkLogo: "dur-dark" }),
  EAST: t("EAST", "Eastern Conference All-Stars", "east"),
  WEST: t("WEST", "Western Conference All-Stars", "west"),
  CHK: t("CHK", "Team Chuck", "chk", { darkLogo: "chk-dark" }),
  KEN: t("KEN", "Team Kenny", "ken", { darkLogo: "ken-dark" }),
  SHQ: t("SHQ", "Team Shaq", "shq", { darkLogo: "shq-dark" }),
  CAN: t("CAN", "Team Candace", "can", { darkLogo: "can-dark" }),
  // The three squads of the 2026 game wear wordmarks ("★USA★", "≡USA≡",
  // "WORLD"), a smear on a score card, so each has a compact mark cut from
  // ESPN's full-size file: the logo's own star, its three stripes (cream on
  // dark grounds, as the dark logo is) and the globe that is World's O
  // (DSPRT-371).
  STARS: t("STRS", "Team Stars", "stars", { compact: "stars-c" }),
  STRIPES: t("STRP", "Team Stripes", "stripes", { darkLogo: "stripes-dark", compact: "stripes-c", compactDark: "stripes-c-dark" }),
};

/**
 * The clubs from outside the league (the international block above). Their
 * players have no NBA profile to open, unlike a former franchise's or an
 * All-Star squad's.
 */
const OUTSIDE_LEAGUE = new Set(["HAP", "RMA", "MCH", "MRC", "FLA", "FRA", "SLA", "BEI", "SHA", "GZL", "MEL", "PER", "SYD", "NZB", "ADL", "CNS", "ULM", "SEM", "LON", "NBR", "ZJL", "QDE", "MTA", "EFS", "MEG", "VAL", "FEN"]);

export function isOutsideLeague(team: OtherTeam | null | undefined): boolean {
  return !!team && OUTSIDE_LEAGUE.has(team.code);
}

/*
 * ABA clubs, for the ABA seasons a legend's career table shows (the backend
 * takes those from Basketball-Reference; see CareerCorrections). The codes are
 * Basketball-Reference's and only mean these clubs on a row marked ABA: KEN is
 * Team Kenny anywhere else. Like FORMER, each club lists the last season (by
 * its closing year) each logo was used, and a code Basketball-Reference kept
 * across a rename lists both names.
 *
 * Logos: sportslogos.net, the club's primary logo from the year in the file
 * name (the closing year of the first season it was used), from the ABA
 * league page; white matte around the ones without transparency removed.
 * `inv` marks the four dark logos that would vanish on a retired player's
 * grey panel (the profile sets a dark theme for its crests in both site
 * themes).
 */
const ABA: Record<string, Array<[lastSeason: number, team: OtherTeam]>> = {
  ANA: [[1968, t("ANA", "Anaheim Amigos", "ana68", inv)]],
  CAR: [
    [1971, t("CAR", "Carolina Cougars", "car70")],
    [1974, t("CAR", "Carolina Cougars", "car72")],
  ],
  DLC: [
    [1970, t("DLC", "Dallas Chaparrals", "dlc68")],
    [1973, t("DLC", "Dallas Chaparrals", "dlc71")],
  ],
  TEX: [[1971, t("TEX", "Texas Chaparrals", "dlc71")]],
  DNR: [
    [1971, t("DNR", "Denver Rockets", "dnr68")],
    [1974, t("DNR", "Denver Rockets", "dnr72")],
  ],
  DNA: [[1976, t("DNA", "Denver Nuggets", "dna75")]],
  MMF: [[1970, t("MMF", "Miami Floridians", "mmf69")]],
  FLO: [
    [1971, t("FLO", "The Floridians", "flo71")],
    [1972, t("FLO", "The Floridians", "flo72", inv)],
  ],
  HSM: [
    [1968, t("HSM", "Houston Mavericks", "hsm68")],
    [1969, t("HSM", "Houston Mavericks", "hsm69")],
  ],
  INA: [[1976, t("INA", "Indiana Pacers", "ina68")]],
  KEN: [
    [1970, t("KEN", "Kentucky Colonels", "ken68")],
    [1976, t("KEN", "Kentucky Colonels", "ken71")],
  ],
  LAS: [
    [1969, t("LAS", "Los Angeles Stars", "las69")],
    [1970, t("LAS", "Los Angeles Stars", "las70")],
  ],
  MMP: [
    [1971, t("MMP", "Memphis Pros", "mmp71")],
    [1972, t("MMP", "Memphis Pros", "mmp72")],
  ],
  MMT: [[1974, t("MMT", "Memphis Tams", "mmt73", inv)]],
  MMS: [[1975, t("MMS", "Memphis Sounds", "mms75")]],
  MNM: [[1968, t("MNM", "Minnesota Muskies", "mnm68")]],
  MNP: [[1969, t("MNP", "Minnesota Pipers", "mnp69")]],
  NJA: [[1968, t("NJA", "New Jersey Americans", "nja68")]],
  NYA: [
    [1972, t("NYA", "New York Nets", "nya69")],
    [1976, t("NYA", "New York Nets", "nya73")],
  ],
  NOB: [
    [1969, t("NOB", "New Orleans Buccaneers", "nob68")],
    [1970, t("NOB", "New Orleans Buccaneers", "nob70")],
  ],
  OAK: [[1969, t("OAK", "Oakland Oaks", "oak68")]],
  PTP: [
    [1968, t("PTP", "Pittsburgh Pipers", "ptp68")],
    [1970, t("PTP", "Pittsburgh Pipers", "ptp70")],
  ],
  PTC: [
    [1971, t("PTC", "Pittsburgh Condors", "ptc71")],
    [1972, t("PTC", "Pittsburgh Condors", "ptc72")],
  ],
  SAA: [[1976, t("SAA", "San Antonio Spurs", "saa74", inv)]],
  SDA: [[1975, t("SDA", "San Diego Conquistadors", "sda73")]],
  SDS: [[1976, t("SDS", "San Diego Sails", "sds76")]],
  SSL: [[1976, t("SSL", "Spirits of St. Louis", "ssl75")]],
  UTS: [[1976, t("UTS", "Utah Stars", "uts71")]],
  VIR: [
    [1971, t("VIR", "Virginia Squires", "vir71")],
    [1974, t("VIR", "Virginia Squires", "vir72")],
    [1976, t("VIR", "Virginia Squires", "vir75")],
  ],
  WSA: [[1970, t("WSA", "Washington Caps", "wsa70")]],
};

/**
 * The ABA club behind a code on an ABA row, with the logo of `seasonEnd`'s
 * era (its closing year). An unknown code keeps its code, on a tile.
 */
export function abaTeam(abbr: string, seasonEnd?: number | null): OtherTeam {
  const eras = ABA[abbr.toUpperCase()];
  if (!eras) return t(abbr, abbr, null);
  return (seasonEnd ? eras.find(([last]) => seasonEnd <= last)?.[1] : null) ?? eras[eras.length - 1][1];
}

/*
 * ESPN sends two of the clubs with no code at all, only a name; a box score
 * or game line that has the name can still find them.
 */
const BY_NAME: Record<string, OtherTeam> = {
  "ulm": FIXED.ULM,
  "ratiopharm ulm": FIXED.ULM,
  "melbourne pnx": FIXED.SEM,
  "south east melbourne phoenix": FIXED.SEM,
};

/**
 * The closing year of a season label: "2007-08" is 2008, and the bare "1971"
 * ESPN uses for older seasons is already one. A date (a game's) counts toward
 * the season that finishes the following summer from August on.
 */
export function seasonEndYear(label: string | null | undefined): number | null {
  if (!label) return null;
  const span = /^(\d{4})-(\d{2})$/.exec(label);
  if (span) return Number(span[1]) + 1;
  if (/^\d{4}$/.test(label)) return Number(label);
  const date = /^(\d{4})-(\d{2})-\d{2}/.exec(label);
  if (date) return Number(date[1]) + (Number(date[2]) >= 8 ? 1 : 0);
  return null;
}

/**
 * Today's club for a code that was once its own (DSPRT-149): "SEA" is the
 * Thunder, "PHW" Golden State, "NJN" Brooklyn. Null for a code that is
 * already current, or a club that never became one of today's 30.
 */
export function franchiseOf(abbr: string | null | undefined): string | null {
  if (!abbr) return null;
  const code = abbr.toUpperCase();
  if (FIXED[code]?.franchise) return FIXED[code].franchise!;
  for (const eras of Object.values(FORMER)) {
    const hit = eras.find(([, team]) => team.code === code);
    if (hit) return hit[1].franchise ?? null;
  }
  return null;
}

/**
 * A former club by its full name as Basketball-Reference prints it
 * ("Seattle SuperSonics", "Kansas City Kings"), with the logo of `seasonEnd`'s
 * era — the contract history's team column (DSPRT-153). Null for a name that
 * was never a former one, or (with a season) one the club didn't yet or no
 * longer went by then.
 */
export function formerTeamByName(name: string, seasonEnd?: number | null): OtherTeam | null {
  const want = name.trim().toLowerCase();
  for (const eras of Object.values(FORMER)) {
    const named = eras.filter(([, t]) => t.name.toLowerCase() === want);
    if (!named.length) continue;
    if (!seasonEnd) return named[named.length - 1][1];
    const hit = named.find(([last]) => seasonEnd <= last);
    if (hit) return hit[1];
  }
  if (!seasonEnd) for (const t of Object.values(FIXED)) if (t.name.toLowerCase() === want) return t;
  return null;
}

/*
 * Basketball-Reference's international pages name some clubs by their city
 * alone ("Sydney" for the Kings).
 */
const ABROAD_ALIASES: Record<string, OtherTeam> = {
  "sydney": FIXED.SYD,
  "perth": FIXED.PERTH,
  "adelaide": FIXED.ADL,
  "cairns": FIXED.CNS,
  "new zealand": FIXED.NZL,
  "melbourne": FIXED.MEL,
  "s.e. melbourne": FIXED.SEM,
  "south east melbourne": FIXED.SEM,
  "ulm": FIXED.ULM,
  "mega": FIXED.MEGA,
  "valencia": FIXED.VALENCIA,
  "fenerbahce": FIXED.FENER,
  "guangzhou loong lions": FIXED.GUANGZHOU,
  "guangzhou long-lions": FIXED.GUANGZHOU,
};

/**
 * A club outside the NBA by the name Basketball-Reference gives it — the club
 * a player came to the league from, in the move bar of his last games. Null
 * for a club there is no crest for; the bar then shows its name alone.
 */
export function clubAbroad(name: string): OtherTeam | null {
  const want = name.trim().toLowerCase();
  if (ABROAD_ALIASES[want]) return ABROAD_ALIASES[want];
  for (const team of Object.values(FIXED)) if (OUTSIDE_LEAGUE.has(team.code) && team.name.toLowerCase() === want) return team;
  return null;
}

/** Every name a club of today's has gone by before, oldest first: the search's "Seattle SuperSonics" finds the Thunder. */
export function formerNames(abbr: string): string[] {
  const names = new Set<string>();
  for (const eras of Object.values(FORMER)) for (const [, team] of eras) if (team.franchise === abbr) names.add(team.name);
  for (const team of Object.values(FIXED)) if (team.franchise === abbr) names.add(team.name);
  return [...names];
}

/*
 * A draft closes the season of the same year, so the season places a code.
 * But three clubs took today's name between that season's end and draft
 * night, and ESPN's feed, which names no club, sends the old one all the
 * same: Brooklyn from April 2012 (as NJ), the Pelicans from April 2013, the
 * Hornets from May 2014.
 */
const RENAMED_BY_DRAFT: Record<string, number> = { NJ: 2012, NOP: 2013, CHA: 2014 };

/** otherTeam for a pick in `year`'s draft: null where the club already went by today's name that night (DSPRT-359). */
export function draftTeam(abbr: string | null | undefined, year?: number | null): OtherTeam | null {
  if (abbr && year && RENAMED_BY_DRAFT[abbr.toUpperCase()] === year) return null;
  return otherTeam(abbr, year);
}

/**
 * The team behind a code, if it isn't one of today's clubs as it is today.
 * `seasonEnd` is the closing year of the season in question (see
 * seasonEndYear); without one, only the codes that are never current match.
 */
export function otherTeam(abbr: string | null | undefined, seasonEnd?: number | null, name?: string | null): OtherTeam | null {
  // On the G League side its codes are its clubs' (DSPRT-474): CAP is the Go-Go, not the 1974 Bullets.
  if (GL_MODE && isGlClub(abbr)) return null;
  if (abbr) {
    const fixed = FIXED[abbr.toUpperCase()];
    if (fixed) {
      // A code that stands for one former name through several logos (NJ, WSB)
      // shows that season's: the 1995 Nets' and not the 2005 ones' (DSPRT-363).
      const era = fixed.franchise && seasonEnd ? FORMER[fixed.franchise]?.find(([last, team]) => seasonEnd <= last && team.name === fixed.name) : undefined;
      return era?.[1] ?? fixed;
    }
    const eras = FORMER[abbr.toUpperCase()];
    if (eras && seasonEnd) {
      for (const [last, team] of eras) if (seasonEnd <= last) return team;
    }
    if (eras || !name) return null;
  }
  return name ? BY_NAME[name.trim().toLowerCase()] ?? null : null;
}
